/*
 * The Private License
 *
 * Copyright (c) 2023 Mahon Media LLC.
 */

import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom/cjs/react-router-dom.min";
import {Box, Button, CircularProgress, Container, Grid, Paper, Typography,} from "@mui/material";
import { DatePicker } from "antd";
import { Calendar } from "antd";
const { RangePicker } = DatePicker;
import Cookies from "js-cookie";
import ReservationData from "../pages/ReservationData";
import ProfileSelect from "./ProfileSelect";
import CustomMultipleSelect from "./CustomMultipleSelect";
import CustomRatePlans from "./CustomRatePlans";

import {PROFILE_OPTIONS} from "../constants/selectOptions";
import {MAIN_ERROR_MESSAGES} from "../constants/errorMessages";

import {useNotification} from "../contexts/NotificationContext";
import {useCustomers} from "../contexts/CustomersContext";
import dayjs from "dayjs";
import {getAllowedUpgrades, getAllowedUpgradeFroms} from "../utils/roomCodes";
import { getBackendBaseUrl } from "../utils/envUtils";

function ReservationForm() {

  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  
  const handleSelect = (value) => {
      const startDate = dateRange[0].startDate;
      const endDate = dateRange[0].endDate;
      if (value.isBefore(dayjs(), "day")) return;

      if (!startDate) {
        setDateRange([{ startDate: value, endDate: null }]);
      } else if (!endDate && value.isAfter(startDate)) {
        setDateRange([{ startDate, endDate: value }]);
      } else {
        setDateRange([{ startDate: value, endDate: null }]);
      }
    };
  // Handles when the UpsellFrom multiselect selection has changed.
  const handleUpsellFromSelectionChanges = (newSelectedUpsellFroms) => {
    // Update which roomcodes have been selected by the user in the upsellFrom selector.
    setSelectedUpsellFrom(newSelectedUpsellFroms);
    // Clear the old upsell to options that were selected, since they may be invalid now.
    setSelectedUpsellTo([])
    // Use matrix to determine which upgrade to options are allowed.
    setUpsellToOptions(getAllowedUpgrades(newSelectedUpsellFroms));
  }
  const [upsellFromOptions, setUpsellFromOptions] = useState([]);
  const [upsellToOptions, setUpsellToOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showReservationData, setShowReservationData] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [selectedRatePlan, setSelectedRatePlan] = useState([]);
  const [selectedUpsellFrom, setSelectedUpsellFrom] = useState([]);
  const [selectedUpsellTo, setSelectedUpsellTo] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const {customers, setCustomers} = useCustomers([]);
  const [rooms, setRooms] = useState();

  const [responseData, setResponseData] = useState([]);
  const [csv, setCsv] = useState([]);

  const [fetchedProfileOptions, setFetchedProfileOptions] = useState([]);
  const [fetchedRatePlanOptions, setFetchedRatePlanOptions] = useState([]);
  const [upsellFromParams, setUpsellFromParams] = useState();
  const [upsellToParams, setUpsellToParams] = useState();
  const [fetchedCodeOptions, setFetchedCodeOptions] = useState([]);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const {setIsOpen, setMessage, setIsFailed} = useNotification();

  const token = Cookies.get("jwtToken");
  // TODO: Change from hardcoded to hit local backend.
  const HOST = getBackendBaseUrl();
  const PROFILE_ENDPOINT = `${HOST}/api/v1/room-types/upsell`;
  const RATE_PLAN_ENDPOINT = `${HOST}/api/v1/rate-plans`;

  const history = useHistory();
  useEffect(() => {
    if (!token) {
      history.push("/");
      setIsOpen(true);
      setIsFailed(true);
      setMessage(MAIN_ERROR_MESSAGES.tokenExpired);
    }
  }, []);
  const handleDateChange = (ranges) => {
    setDateRange([ranges.selection]);
  };

  const handleSelectCode = (e) => {
    e.preventDefault();
    setSelectedCodes(e.target.value);
  };

  const handleSelectRatePlan = (e) => {
    e.preventDefault();
    setSelectedRatePlan(e.target.value);
  };

  const handleSelectProfile = async (e) => {
    setSelectedProfile(e.target.value);
    setIsProfileLoading(true);
    setFetchedProfileOptions([]);
    setUpsellFromOptions([])
    setUpsellToOptions([])
    setFetchedRatePlanOptions([]);
    setSelectedCodes([]);
    setSelectedRatePlan([]);
    const notificationName =
      e.target.value === "sandbox" ? "Opera Sandbox" : "Beacon Grand";
    try {
      const profileResponse = await fetch(
        `${PROFILE_ENDPOINT}?env=${e.target.value.toLowerCase()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const ratePlanResponse = await fetch(
        `${RATE_PLAN_ENDPOINT}?env=${e.target.value.toLowerCase()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (profileResponse.status === 401) {
        throw new Error(response.status);
      }
      if (!profileResponse.ok) {
        throw new Error("Network response was not ok");
      }
      const options = await profileResponse.json();
      setRooms(options);
      const ratePlans = await ratePlanResponse.json();
      const codes = options.map(item => item.code);
      setFetchedProfileOptions(codes);
      setUpsellFromOptions(getAllowedUpgradeFroms(codes))
      setFetchedRatePlanOptions(ratePlans);
      setIsOpen(true);
      setIsFailed(false);
      setMessage(`Profile is now set to ${notificationName}`);
    } catch (error) {
      const errorMessage =
        MAIN_ERROR_MESSAGES[error.message] || MAIN_ERROR_MESSAGES.default;
      setIsOpen(true);
      setIsFailed(true);
      setMessage(errorMessage);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const profileResponse = await fetch(
      `${PROFILE_ENDPOINT}?env=${selectedProfile}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (profileResponse.status === 401) {
      throw new Error(response.status);
    }
    if (!profileResponse.ok) {
      throw new Error("Network response was not ok");
    }
    const options = await profileResponse.json();
    setRooms(options);
    const selectedStartDate = dateRange[0].startDate;
    const selectedEndDate = dateRange[0].endDate;
    console.log(selectedStartDate);
    console.log(selectedEndDate);
    const adjustedStartDate = new Date(selectedStartDate);
    const adjustedEndDate = new Date(selectedEndDate);
    const formattedStartDate = new Date(
      `${adjustedStartDate.getFullYear()
      }-${String(101 + adjustedStartDate.getMonth()).substr(1)
      }-${String(100 + adjustedStartDate.getDate()).substr(1)
      }`
    ).toISOString().substring(0, 10);
    const formattedEndDate = new Date(
      `${adjustedEndDate.getFullYear()
      }-${String(101 + adjustedEndDate.getMonth()).substr(1)
      }-${String(100 + adjustedEndDate.getDate()).substr(1)
      }`
    ).toISOString().substring(0, 10);
    console.log(formattedStartDate);
    console.log(formattedEndDate);
    const ratePlansParams = selectedRatePlan
      .map((value) => `ratePlan=${value}`)
      .join("&");
    const upsellFromParams = selectedUpsellFrom
      .map((value) => `upsellFrom=${value}`)
      .join("&");
    const upsellToParams = selectedUpsellTo
      .map((value) => `upsellTo=${value}`)
      .join("&");
    setUpsellFromParams(upsellFromParams);
    setUpsellToParams(upsellToParams);
    const url = `${HOST}/api/v1/inventory-reservations?${upsellFromParams}&${upsellToParams}&startDate=${formattedStartDate}&endDate=${formattedEndDate}&env=${selectedProfile}&${ratePlansParams}`;
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(response.status);
      }
      const data = await response.json();
      setResponseData(data);
      // Filter down customers to remove noise by removing ones without
      // email addresses on file.
      setCustomers(data.upsellFrom.customers.filter(customer => customer.email &&
                        (!["", "none"].includes(customer.email.trim().toLowerCase()))));
      setShowReservationData(true);
    } catch (error) {
      const errorMessage =
        MAIN_ERROR_MESSAGES[error.message] || MAIN_ERROR_MESSAGES.default;
      setIsOpen(true);
      setIsFailed(true);
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box sx={{
        display: "flex",
        flexDirection: 'column'
      }}>
        <Typography
          variant="h4"
          style={{
            padding: "20px",
            marginTop: "20px",
            fontWeight: "300",
          }}
          gutterBottom
        >
          OHIP Upsell Engine
          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ marginTop: "-30px" }}
          >
            <Button href={"/upsell/room-types"}>
              Room Types
            </Button>
          </Box>
        </Typography>
      </Box>
      <div style={{marginBottom: "20px"}}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper elevation={3} style={{padding: "20px"}}>
            <Typography strong>
                    Start Date:{" "}
                    {dateRange[0].startDate ? dateRange[0].startDate.format("YYYY-MM-DD") : "Not selected"}
                  </Typography>
                  <br />
                  <Typography strong>
                    End Date:{" "}
                    {dateRange[0].endDate ? dateRange[0].endDate.format("YYYY-MM-DD") : "Not selected"}
                  </Typography>
                <Calendar
                        fullscreen
                        onSelect={handleSelect}
                        dateFullCellRender={(date) => {
                          const { startDate, endDate } = dateRange[0];
                          const isSelected = startDate?.isSame(date, "day") || endDate?.isSame(date, "day");
                          const isInRange = startDate && endDate && date.isAfter(startDate) && date.isBefore(endDate);
                          const isPast = date.isBefore(dayjs(), "day");
                          return (
                            <div
                              style={{
                                padding: 8,
                                background: isSelected
                                  ? "#1890ff"
                                  : isInRange
                                  ? "#bae7ff"
                                  : "transparent",
                                color: isSelected ? "#fff" : isPast ? "#d9d9d9" : undefined,
                                borderRadius: "4px",
                                textDecoration: isPast ? "line-through" : "none",
                                cursor: isPast ? "not-allowed" : "pointer",
                              }}
                            >
                              {date.date()}
                            </div>
                          );
                        }}
                      />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper elevation={3} style={{padding: "20px"}}>
              <form onSubmit={handleSubmit}>
                {/* Profile */}
                <ProfileSelect
                  label="Choose a profile"
                  handleSelectValue={handleSelectProfile}
                  isItemLoading={isProfileLoading}
                  selectedValue={selectedProfile}
                  options={PROFILE_OPTIONS}
                />

                {/* Rate Plan */}
                <CustomRatePlans
                  label="Rate Plan"
                  options={fetchedRatePlanOptions}
                  selectedValue={selectedRatePlan}
                  setSelectedValue={setSelectedRatePlan}
                  labelId="rate-plan"
                  isFullWidth={true}
                />

                {/* UpsellFrom */}
                <CustomMultipleSelect
                  label="Upsell From"
                  options={upsellFromOptions}
                  selectedValue={selectedUpsellFrom}
                  setSelectedValue={handleUpsellFromSelectionChanges}
                  labelId="upsell-from"
                  isFullWidth={true}
                />

                {/* UpsellTo */}
                <CustomMultipleSelect
                  label="Upsell To"
                  options={upsellToOptions}
                  selectedValue={selectedUpsellTo}
                  setSelectedValue={setSelectedUpsellTo}
                  labelId="upsell-to"
                  isFullWidth={true}
                />

                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    fullWidth
                  >
                    Submit
                  </Button>
                </Box>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </div>

      {loading ? (
        <CircularProgress
          sx={{
            display: "block",
            m: "0 auto",
            mt: 5,
          }}
        />
      ) : showReservationData ? (
        <ReservationData
          data={responseData}
          rooms={rooms}
          from={upsellFromParams}
          to={upsellToParams}
          env={selectedProfile}
          rates={selectedRatePlan.map((value) => `ratePlan=${value}`).join("&")}
        />
      ) : null}
    </Container>
  );
}

export default ReservationForm;
